"use client";

import { CheckCircle2, Send } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Button, Input } from "@/components/ui";
import api from "@/lib/api";
import { cn } from "@/lib/cn";
import { useToastStore } from "@/store/toast";

interface FieldErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
  order_number?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ContactForm() {
  const showToast = useToastStore((s) => s.showToast);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [website, setWebsite] = useState("");

  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formError, setFormError] = useState("");

  function validate(): boolean {
    const next: FieldErrors = {};
    if (!name.trim()) next.name = "Name is required.";
    if (!email.trim()) next.email = "Email is required.";
    else if (!EMAIL_RE.test(email.trim()))
      next.email = "Please enter a valid email address.";
    if (!subject.trim()) next.subject = "Subject is required.";
    if (!message.trim()) next.message = "Message is required.";
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError("");
    if (success) return;

    // Honeypot: bots fill the hidden field — pretend success without
    // hitting the API (matches the backend's silent-success behaviour).
    if (website) {
      setSuccess(true);
      return;
    }

    if (!validate()) return;

    setSubmitting(true);
    try {
      await api.post("/contact/", {
        name: name.trim(),
        email: email.trim(),
        subject: subject.trim(),
        message: message.trim(),
        order_number: orderNumber.trim() || undefined,
        website: "",
      });
      setSuccess(true);
      showToast("Thank you for contacting us.", "success");
    } catch (err) {
      const error = err as Error & {
        status?: number;
        data?: Record<string, unknown>;
      };
      if (error.status === 400 && error.data) {
        const fieldErrors: FieldErrors = {};
        for (const [key, value] of Object.entries(error.data)) {
          if (key === "detail") continue;
          const msg = Array.isArray(value) ? value.join(" ") : String(value);
          if (
            key in fieldErrors ||
            ["name", "email", "subject", "message", "order_number"].includes(
              key,
            )
          ) {
            fieldErrors[key as keyof FieldErrors] = msg;
          }
        }
        if (Object.keys(fieldErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...fieldErrors }));
        }
        setFormError("Please fix the errors below.");
      } else {
        setFormError(
          error.message || "Something went wrong. Please try again later.",
        );
      }
      showToast("Could not submit the form. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <div
        data-testid="contact-form"
        className="border-ink-200 rounded-lg border bg-white p-6"
      >
        <div
          data-testid="contact-success"
          className="flex flex-col items-center gap-2 py-6 text-center"
        >
          <CheckCircle2 className="h-8 w-8 text-green-600" />
          <p className="text-ink-900 font-medium">
            Thank you for contacting us.
          </p>
          <p className="text-ink-600 text-sm">
            We will get back to you within 1–2 business days.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      data-testid="contact-form"
      onSubmit={handleSubmit}
      noValidate
      className="border-ink-200 space-y-4 rounded-lg border bg-white p-6"
    >
      <h2 className="text-ink-900 text-lg font-semibold">Send us a message</h2>

      {/* Honeypot — visually hidden, off-screen, not for humans. */}
      <div
        aria-hidden="true"
        className="absolute left-[-9999px] h-0 w-0 overflow-hidden"
      >
        <label htmlFor="contact-website">Website (leave empty)</label>
        <input
          id="contact-website"
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          data-testid="contact-website"
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label
            htmlFor="contact-name"
            className="text-ink-700 mb-1.5 block text-sm font-medium"
          >
            Name
          </label>
          <Input
            id="contact-name"
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            hasError={Boolean(errors.name)}
            placeholder="Your name"
            data-testid="contact-name"
          />
          {errors.name && (
            <p
              data-testid="contact-name-error"
              className="text-danger mt-1 text-xs"
            >
              {errors.name}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="contact-email"
            className="text-ink-700 mb-1.5 block text-sm font-medium"
          >
            Email
          </label>
          <Input
            id="contact-email"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            hasError={Boolean(errors.email)}
            placeholder="you@example.com"
            data-testid="contact-email"
          />
          {errors.email && (
            <p
              data-testid="contact-email-error"
              className="text-danger mt-1 text-xs"
            >
              {errors.email}
            </p>
          )}
        </div>
      </div>

      <div>
        <label
          htmlFor="contact-subject"
          className="text-ink-700 mb-1.5 block text-sm font-medium"
        >
          Subject
        </label>
        <Input
          id="contact-subject"
          name="subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          hasError={Boolean(errors.subject)}
          placeholder="How can we help?"
          data-testid="contact-subject"
        />
        {errors.subject && (
          <p
            data-testid="contact-subject-error"
            className="text-danger mt-1 text-xs"
          >
            {errors.subject}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="contact-order-number"
          className="text-ink-700 mb-1.5 block text-sm font-medium"
        >
          Order number <span className="text-ink-400">(optional)</span>
        </label>
        <Input
          id="contact-order-number"
          name="order_number"
          value={orderNumber}
          onChange={(e) => setOrderNumber(e.target.value)}
          placeholder="e.g. ZYL-2026-000001"
          data-testid="contact-order-number"
        />
      </div>

      <div>
        <label
          htmlFor="contact-message"
          className="text-ink-700 mb-1.5 block text-sm font-medium"
        >
          Message
        </label>
        <textarea
          id="contact-message"
          name="message"
          value={message}
          rows={5}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Tell us how we can help…"
          data-testid="contact-message"
          className={cn(
            "focus:ring-gold-400 text-ink-900 placeholder:text-ink-400 w-full rounded-md border bg-white p-2.5 text-sm focus:ring-2 focus:outline-none",
            errors.message ? "border-danger" : "border-ink-200",
          )}
        />
        {errors.message && (
          <p
            data-testid="contact-message-error"
            className="text-danger mt-1 text-xs"
          >
            {errors.message}
          </p>
        )}
      </div>

      {formError && (
        <p
          data-testid="contact-error"
          className="text-danger text-sm"
          role="alert"
        >
          {formError}
        </p>
      )}

      <Button
        type="submit"
        isLoading={submitting}
        data-testid="contact-submit"
        className="w-full sm:w-auto"
      >
        <Send className="h-4 w-4" />
        Send Message
      </Button>
    </form>
  );
}
